/**
 * Storage persistence (spec §10): ask the browser once, on the first save, to keep our storage through
 * eviction (`navigator.storage.persist()`). Guarded when the API is missing; a refusal or error is logged only.
 */
import { logger } from '../core/logger'

/** The part of `StorageManager` we call. */
export interface PersistManager {
  persist(): Promise<boolean>
}

/** A function that calls `manager.persist()` at most once and then returns the first answer. */
export function createPersistRequest(manager: PersistManager | undefined): () => Promise<boolean> {
  let answer: Promise<boolean> | null = null
  return () => {
    if (answer === null) {
      answer =
        manager === undefined || typeof manager.persist !== 'function'
          ? Promise.resolve(false)
          : manager.persist().then(
              (granted) => {
                logger.info(`save: persistent storage ${granted ? 'granted' : 'not granted'}`)
                return granted
              },
              (err: unknown) => {
                logger.warn('save: navigator.storage.persist() failed', err)
                return false
              },
            )
    }
    return answer
  }
}

function browserManager(): PersistManager | undefined {
  return typeof navigator === 'undefined' ? undefined : navigator.storage
}

let shared: (() => Promise<boolean>) | null = null

/** The app-wide request (bound to `navigator.storage` on first use). */
export function requestPersistence(): Promise<boolean> {
  shared ??= createPersistRequest(browserManager())
  return shared()
}
